import { useCallback } from "react"
import { useMessage } from "hooks/message.hook"
import { isAxiosError } from "helpers/utils.helper"

type ErrorObject = {
	[key: string]: string[] | ErrorObject
}

const formatKey = (key: string): string => {
	return key
		.split("_")
		.map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
		.join(" ")
}

export const useApiErrorFormatter = () => {
	const { error: errorMessage } = useMessage()

	// Memoized so consumers can safely list `formatError` in effect/callback dependency arrays
	// without churning (e.g. the quotation viewer's copy/paste listener effect). `errorMessage`
	// from useMessage is itself memoized, so this identity is stable across renders.
	const formatError = useCallback(
		(e: unknown) => {
			const handleErrorObject = (obj: ErrorObject, parentKey?: string) => {
				if (typeof obj !== "object" || obj === null) return

				Object.entries(obj).forEach(([key, value]) => {
					const formattedKey = formatKey(key)
					const fullKey = parentKey ? `${parentKey} - ${formattedKey}` : formattedKey

					if (Array.isArray(value)) {
						if (value.length > 0 && typeof value[0] === "string") {
							const message = value[0]
							errorMessage(`${fullKey}: ${message}`)
						} else {
							value.forEach((item) => {
								if (typeof item === "object" && item !== null) {
									handleErrorObject(item as ErrorObject, fullKey)
								}
							})
						}
					} else if (typeof value === "string") {
						// Handle direct string values like {"error": "message"}
						errorMessage(value)
					} else if (typeof value === "object" && value !== null) {
						handleErrorObject(value, fullKey)
					}
				})
			}

			if (isAxiosError(e)) {
				const data = e.response?.data
				const status = e.response?.status

				// Handle all 4xx client errors (400-499)
				if (status && status >= 400 && status < 500 && data) {
					// Handle array of error messages
					if (Array.isArray(data)) {
						const stringErrors = data.filter((item) => typeof item === "string")
						if (stringErrors.length > 0) {
							// Display all error messages
							stringErrors.forEach((message) => errorMessage(message))
						} else {
							errorMessage("Something went wrong. Please try again later.")
						}
					} else if (typeof data === "object") {
						// Handle object with error fields
						handleErrorObject(data as ErrorObject)
					} else {
						errorMessage("Something went wrong. Please try again later.")
					}
				} else {
					errorMessage("Something went wrong. Please try again later.")
				}
			}
		},
		[errorMessage],
	)

	return { formatError }
}
